import { Camera } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrganizationAssignedDevices, OrganizationAssignedDevice } from "@/hooks/use-organization-assigned-devices";

interface OrganizationDevicesTabProps {
  organizationId: string;
  organizationName: string;
}

const getStatusBadgeVariant = (status: string | null) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'default';
    case 'hibernate':
      return 'secondary';
    case 'offline':
      return 'destructive';
    default:
      return 'outline';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatCredits = (credits: number) => {
  return credits.toLocaleString();
};

const formatContract = (years: number | null, plan: string | null) => {
  if (!years && !plan) return '-';
  const yearText = years ? `${years}yr` : '';
  const planText = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : '';
  return [yearText, planText].filter(Boolean).join(' ');
};

export const OrganizationDevicesTab = ({ 
  organizationId, 
  organizationName 
}: OrganizationDevicesTabProps) => {
  const { data: devices = [], isLoading } = useOrganizationAssignedDevices(organizationId);

  const totalCredits = devices.reduce((sum, d) => sum + d.total_credits, 0);

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Camera className="h-12 w-12 text-slate-400 mb-4" />
        <h3 className="text-lg font-medium text-slate-700">No devices assigned</h3>
        <p className="text-sm text-slate-500 mt-1">
          Devices will appear here once orders are fulfilled.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">
          Devices at {organizationName}
        </h3>
        <Badge variant="secondary" className="text-sm">
          {devices.length} device{devices.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Device</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead>Renewal</TableHead>
              <TableHead className="text-right">Credits</TableHead>
              <TableHead>Contract</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.map((device) => (
              <TableRow key={device.device_id}>
                <TableCell>
                  <div className="space-y-0.5">
                    <div className="font-medium text-slate-800">
                      {device.device_name || device.device_id}
                    </div>
                    {device.device_description && (
                      <div className="text-xs text-slate-500 truncate max-w-[200px]">
                        {device.device_description}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusBadgeVariant(device.device_status)}>
                    {device.device_status || 'Unknown'}
                  </Badge>
                </TableCell>
                <TableCell className="text-slate-600">
                  {formatDate(device.assigned_at)}
                </TableCell>
                <TableCell className="text-slate-600">
                  {(() => {
                    const renewalDate = new Date(device.assigned_at);
                    renewalDate.setFullYear(renewalDate.getFullYear() + 1);
                    return formatDate(renewalDate.toISOString());
                  })()}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {device.total_credits > 0 ? formatCredits(device.total_credits) : '-'}
                </TableCell>
                <TableCell className="text-slate-600">
                  {formatContract(device.coverage_years, device.plan_type)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalCredits > 0 && (
        <div className="flex justify-end text-sm text-slate-600">
          Total: <span className="font-medium ml-1">{formatCredits(totalCredits)}</span> credits allocated
        </div>
      )}
    </div>
  );
};
